// Copyright (c) 2025 Audere, All Rights Reserved

import { Box, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import {
  useLazyGetReportCardQuery,
  useLazyListPhotosetsQuery,
} from "../features/data/projectApiSlice";
import ConfusionMatrix from "./ConfusionMatrix";
import DashboardTable from "./DashboardTable";
import ReportsHeader from "./ReportsHeader";
import { getRDTName } from "../utils/string";
import { calcPercent, formatNumber } from "../utils/number";
import {
  tableColumn,
  type RdtBrandDetected,
  type ReportsContainerProps,
  type TableRow,
} from "../types/projects";
import { createStyleSheet } from "../utils/styles";
import { useParams } from "react-router";

const ReportsContainer = ({
  selectedTest,
  handleRDTSelection,
  latestModel,
}: ReportsContainerProps) => {
  const { t } = useTranslation("ReportsContainer");
  const [getReportCard, { data }] = useLazyGetReportCardQuery();
  const params = useParams();
  const [listPhotosets] = useLazyListPhotosetsQuery();
  const [selectedOption, setSelectedOption] = useState<string>("");

  const availableOptions = Object.keys(data?.report_cards || {});
  const dropdownOptions = availableOptions.map(option => ({
    label: option.replace(/_/g, " "),
    value: option,
  }));

  useEffect(() => {
    const projectUid = params.projectUid;
    if (projectUid && selectedTest) {
      listPhotosets({ rdtUid: selectedTest.rdt.rdt_uid })
        .unwrap()
        .then(response => {
          const photoset = response.photosets.find(
            p => p.rdt_uid === selectedTest.rdt.rdt_uid && p.purpose === "DEVELOPMENT"
          );

          if (!photoset) return;

          const reportCardRequestParam = {
            organization_uid: selectedTest.rdt.organization_uid,
            project_uid: selectedTest.projectUid,
            rdt: {
              organization_uid: selectedTest.rdt.organization_uid,
              rdt_uid: selectedTest.rdt.rdt_uid,
            },
            model: {
              organization_uid: latestModel?.organization_uid,
              model_uid: latestModel?.model_uid,
            },
            photoset: {
              organization_uid: selectedTest.rdt.organization_uid,
              photoset_uid: photoset.photoset_uid,
            },
          };

          return getReportCard(reportCardRequestParam);
        })
        .catch(error => {
          console.error("Error fetching photosets or report card:", error);
        });
    }
  }, [listPhotosets, getReportCard, params.projectUid, selectedTest, latestModel]);

  useEffect(() => {
    if (dropdownOptions.length > 0 && !selectedOption) {
      setSelectedOption(dropdownOptions[0].value);
    }
  }, [dropdownOptions, selectedOption]);

  const report = data?.report_cards?.[selectedOption];

  const column = useMemo(() => (label: string, key: string) => tableColumn(label, key), []);

  const metricsData = useMemo<TableRow[]>(() => {
    if (!report) return [];
    const {
      total_images,
      total_images_passing_filters,
      percent_images_failing_filters,
      weighted_recall,
      weighted_precision,
      weighted_f1,
      weighted_f1_confidence_interval,
      total_regular_positives,
      regular_positive_accuracy,
      total_faint_positives,
      faint_positive_accuracy,
      sensitivity,
    } = report;

    return [
      { label: t("metrics.totalImages"), value: total_images },
      { label: t("metrics.totalImagesPassingFilters"), value: total_images_passing_filters },
      {
        label: t("metrics.percentImagesFailingFilters"),
        value: formatNumber(percent_images_failing_filters, 4),
      },
      { label: t("metrics.weightedRecall"), value: formatNumber(weighted_recall, 4) },
      { label: t("metrics.weightedPrecision"), value: formatNumber(weighted_precision, 4) },
      { label: t("metrics.weightedF1"), value: formatNumber(weighted_f1, 4) },
      {
        label: t("metrics.weightedF1CI"),
        value: weighted_f1_confidence_interval
          ? `(${formatNumber(weighted_f1_confidence_interval.low, 4)}, ${formatNumber(
              weighted_f1_confidence_interval.high,
              4
            )})`
          : "-",
      },
      { label: t("metrics.totalRegularPositives"), value: total_regular_positives },
      {
        label: t("metrics.regularPositiveAccuracy"),
        value: formatNumber(regular_positive_accuracy, 4),
      },
      { label: t("metrics.totalFaintPositives"), value: total_faint_positives },
      {
        label: t("metrics.faintPositiveAccuracy"),
        value: formatNumber(faint_positive_accuracy, 4),
      },
      { label: t("metrics.sensitivity"), value: formatNumber(sensitivity, 4) },
    ];
  }, [report, t]);

  const outcomeData = useMemo<TableRow[]>(() => {
    if (!report) return [];
    const { filtered_true_outcome_distribution } = report;
    return Object.entries(filtered_true_outcome_distribution.num_photos).map(([label, value]) => ({
      label,
      value,
      percentage: `${formatNumber(filtered_true_outcome_distribution.percent_photos[label], 1)}%`,
    }));
  }, [report]);

  const rdtData = useMemo<TableRow[]>(() => {
    if (!report) return [];
    const { rdt_brand_detected_distribution } = report;
    const totalCount = rdt_brand_detected_distribution.reduce((sum, { n }) => sum + n, 0);
    return rdt_brand_detected_distribution.map(({ n, ...rest }) => ({
      label: getRDTName(rest as RdtBrandDetected),
      value: n,
      percentage: calcPercent(n, totalCount),
    }));
  }, [report]);

  const filterData = useMemo<TableRow[]>(() => {
    if (!report) return [];
    const { filter_results, total_images } = report;
    return Object.entries(filter_results).map(([label, { n }]) => ({
      label,
      value: n,
      percentage: calcPercent(n, total_images),
    }));
  }, [report]);

  const tableConfigs = useMemo(() => {
    const configs = [
      {
        data: metricsData,
        columns: [
          { key: "label", i18n: "columns.metric" },
          { key: "value", i18n: "columns.metricValue" },
        ],
      },
      {
        data: outcomeData,
        columns: [
          { key: "label", i18n: "columns.outcome" },
          { key: "value", i18n: "columns.photoCount" },
          { key: "percentage", i18n: "columns.photoPercent" },
        ],
      },
      {
        data: rdtData,
        columns: [
          { key: "label", i18n: "columns.rdtBrandDetected" },
          { key: "value", i18n: "columns.imagesCount" },
          { key: "percentage", i18n: "columns.imagesPercent" },
        ],
      },
      {
        data: filterData,
        columns: [
          { key: "label", i18n: "columns.filter" },
          { key: "value", i18n: "columns.failedCount" },
          { key: "percentage", i18n: "columns.failedPercent" },
        ],
      },
    ];

    return configs.map(({ data, columns }) => ({
      data,
      columns: columns.map(({ key, i18n }) => column(t(i18n), key)),
    }));
  }, [column, t, metricsData, outcomeData, rdtData, filterData]);

  if (!report) return null;

  return (
    <Box sx={styles.container}>
      {selectedTest && (
        <ReportsHeader
          selectedTest={selectedTest}
          outcomeDistribution={outcomeData}
          dropdownOptions={dropdownOptions}
          setSelectedOption={setSelectedOption}
          selectedOption={selectedOption}
          handleRDTSelection={handleRDTSelection}
        />
      )}
      <Grid container spacing={2} sx={{ p: 3 }}>
        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
          <DashboardTable columns={tableConfigs[0].columns} data={tableConfigs[0].data} />
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
          <ConfusionMatrix confusionMatrixData={report.filtered_confusion_matrix} />
        </Grid>
        {tableConfigs.slice(1).map((table, idx) => (
          <Grid size={{ xs: 12, sm: 12, md: 6 }} key={idx}>
            <DashboardTable columns={table.columns} data={table.data} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ReportsContainer;

const styles = createStyleSheet({
  container: {
    width: "100%",
  },
});
